import { supabase as supabaseEmpresas } from './supabase/supabaseClient.js';

/**
 * `empresa_empleadores` NO se toca con supabaseClient: ese cliente llega como
 * anon y la tabla tiene RLS que exige `clerk_org_id = jwt.org_id`, así que el
 * INSERT daba 42501, el SELECT devolvía [] con HTTP 200 y el UPDATE no matcheaba
 * ninguna fila. Todo pasa por /api/empresas, que valida la sesión de Clerk y
 * escribe con service_role — mismo comportamiento en dev y en QA, sin depender de
 * las políticas de cada base. Ver el encabezado de api/empresas/index.js.
 *
 * El token se recibe por parámetro (`getToken` de `useAuth()`) en vez de importar
 * Clerk aquí, igual que auditoriaService, para que el servicio siga siendo
 * testeable sin contexto de React.
 */
const empresasRequest = async (getToken, path = '', options = {}) => {
  const token = await getToken();
  if (!token) {
    const err = new Error('Sesión no válida');
    err.status = 401;
    throw err;
  }

  const res = await fetch(`/api/empresas${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    const detalle = await res.json().catch(() => ({}));
    const err = new Error(detalle.error || res.statusText || 'Error en /api/empresas');
    err.status = res.status;
    throw err;
  }

  return res.json();
};

export const getEmpresas = async (getToken) => {
  const data = await empresasRequest(getToken);
  return data || [];
};

/**
 * Crea la organización de Clerk de la empresa y la vincula (`clerk_org_id`).
 * Opcionalmente invita al administrador por email.
 *
 * Va por un endpoint aparte y no por el PATCH del formulario a propósito:
 * `clerk_org_id` está fuera de la whitelist de campos editables, porque quien
 * pueda escribirlo se apropia de la empresa vía las políticas RLS por org. La
 * vinculación es una acción explícita, no un campo más del form.
 */
export const crearOrgClerk = async (getToken, id, emailAdmin = null) =>
  empresasRequest(getToken, `/clerk-org?id=${encodeURIComponent(id)}`, {
    method: 'POST',
    body: emailAdmin ? { email_admin: emailAdmin } : {},
  });

export const getPlanesHabilitados = async (empresaId) => {
  const { data, error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .select('id, plan_id, nombre_interno, precio_uf_override, ambiente, activo, poliza_colectiva_id, plan:planes(nombre, solo_empresa, producto_id)')
    .eq('empresa_id', empresaId)
    .eq('activo', true)
    .order('created_at');
  if (error) throw error;
  return data || [];
};

// Empresa + plan habilitado ya vinculados a esta póliza (si existe alguno) —
// sin esto, reabrir una póliza en modo edición mostraba el paso 6 siempre en
// blanco, dando la impresión de que la vinculación no se había guardado.
export const getEmpresaPlanHabilitadoPorPoliza = async (polizaColectivaId) => {
  const { data, error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .select('id, empresa_id')
    .eq('poliza_colectiva_id', polizaColectivaId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

export const setPolizaColectiva = async (empresaPlanHabilitadoId, polizaColectivaId) => {
  const { data, error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .update({ poliza_colectiva_id: polizaColectivaId })
    .eq('id', empresaPlanHabilitadoId)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const addPlanHabilitado = async (empresaId, planId, opts = {}) => {
  const ambiente = opts.ambiente || 'empleados';

  const { data: existing } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .select('id')
    .eq('empresa_id', empresaId)
    .eq('plan_id', planId)
    .eq('ambiente', ambiente)
    .maybeSingle();

  if (existing) {
    const { data, error } = await supabaseEmpresas
      .from('empresa_planes_habilitados')
      .update({
        nombre_interno: opts.nombre_interno || null,
        precio_uf_override: opts.precio_uf_override || null,
        activo: true
      })
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .insert({
      empresa_id: empresaId,
      plan_id: planId,
      nombre_interno: opts.nombre_interno || null,
      precio_uf_override: opts.precio_uf_override || null,
      ambiente,
      activo: true
    })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const removePlanHabilitado = async (id) => {
  const { error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .update({ activo: false })
    .eq('id', id);
  if (error) throw error;
};

export const getPlanIdsUsadosEnEmpresas = async () => {
  const { data, error } = await supabaseEmpresas
    .from('empresa_planes_habilitados')
    .select('plan_id')
    .eq('activo', true);
  if (error) throw error;
  return new Set((data || []).map(r => r.plan_id));
};

export const getPlanesCentral = async () => {
  const { data, error } = await supabaseEmpresas
    .from('planes')
    .select('id, nombre, descripcion, uf_base, activo, solo_empresa, producto_id, producto:producto(id, nombre)')
    .eq('activo', true)
    .order('nombre');
  if (error) throw error;
  return data || [];
};

export const createEmpresa = async (getToken, empresaData) =>
  empresasRequest(getToken, '', { method: 'POST', body: empresaData });

// El endpoint pone `updated_at` y filtra los campos que no son editables.
export const updateEmpresa = async (getToken, id, empresaData) =>
  empresasRequest(getToken, `?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: empresaData,
  });

export const toggleEmpresaEstado = async (getToken, id, estado) =>
  empresasRequest(getToken, `?id=${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: { estado },
  });
